import { coreApi } from '@shared/api/coreApi';
import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';

/**
 * Join-a-tenant + onboarding-gate API — mirrors the Angular member-portal
 * `JoinTenantService` and the onboarding gate the `OnboardingGuard` reads.
 *
 * SSO + tenant switching itself lives in `features/auth/api.ts`
 * (`/portal-auth/login`, `/portal-auth/switch-tenant`,
 * `/portal-auth/joinable-tenants`) — this module covers the *join* journey:
 * discover a community, request to join, then clear the onboarding gate.
 */

export interface TenantSearchResult {
  tenant_id: string;
  name: string;
  /** Member-portal hostname for Core resolve-domain + Tenant API signup. */
  resolution_domain: string | null;
  logo_small_url?: string | null;
  logo_large_url?: string | null;
}

export interface JoinRequestMemberData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  member_type_id?: string;
  id_number?: string;
  membership_number?: string;
  location_id?: string;
  property_residency_kind?: 'resident' | 'non_resident' | 'temporary_resident';
  property_relationship_type_id?: string;
}

export interface JoinRequestResult {
  member_request_id: string;
  onboarding_id?: string;
  flow_instance_id?: string;
}

export interface JoinStatus {
  member_request_id: string;
  tenant_id: string;
  request_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * The shape returned by both `GET /members/me/onboarding-gate` (post-login)
 * and `GET /public/member-requests/join/onboarding-gate` (join flow).
 */
export interface OnboardingGate {
  requires_onboarding: boolean;
  onboarding_status?: string | null;
  flow_instance_id?: string | null;
  onboarding_id?: string | null;
  flow_template_id?: string | null;
}

/** Search joinable tenants within a root scope (Core API, public). */
export async function searchTenantsByRoot(
  rootTenantId: string,
  query?: string,
): Promise<TenantSearchResult[]> {
  const { data } = await coreApi.get<{ tenants?: TenantSearchResult[] }>(
    '/tenants/search-by-root',
    { params: { root_tenant_id: rootTenantId, q: query?.trim() || undefined } },
  );
  return data.tenants ?? [];
}

/** Submit a request to join a tenant (Tenant API, public). */
export async function joinTenant(
  coreUserId: string,
  memberData?: JoinRequestMemberData,
  rootTenantId?: string,
): Promise<JoinRequestResult> {
  const { data } = await tenantApi.post('/public/member-requests/join', {
    core_user_id: coreUserId,
    ...(memberData && Object.keys(memberData).length ? { member_data: memberData } : {}),
    ...(rootTenantId ? { root_tenant_id: rootTenantId } : {}),
  });
  return unwrap<JoinRequestResult>(data);
}

/** Latest join request for this Core user on the active tenant. */
export async function fetchJoinStatus(coreUserId: string): Promise<JoinStatus | null> {
  const { data } = await tenantApi.get('/public/member-requests/join/status', {
    params: { core_user_id: coreUserId },
  });
  return unwrap<JoinStatus | null>(data);
}

/** Onboarding gate for a pending join request (pre tenant-context switch). */
export async function fetchJoinOnboardingGate(coreUserId: string): Promise<OnboardingGate> {
  const { data } = await tenantApi.get('/public/member-requests/join/onboarding-gate', {
    params: { core_user_id: coreUserId },
  });
  return unwrap<OnboardingGate>(data);
}

/**
 * Post-login onboarding gate for the authenticated member. The
 * `OnboardingGuard` equivalent: if `requires_onboarding`, route the member
 * into `/onboarding` with the supplied flow/instance ids before letting
 * them into the app.
 */
export async function fetchMyOnboardingGate(): Promise<OnboardingGate> {
  const { data } = await tenantApi.get('/members/me/onboarding-gate');
  return unwrap<OnboardingGate>(data);
}

/** Build the onboarding route a gate result should redirect to. */
export function onboardingRouteForGate(gate: OnboardingGate): string | null {
  if (!gate.requires_onboarding) return null;
  if (gate.flow_instance_id) return `/onboarding?instance=${gate.flow_instance_id}`;
  if (gate.flow_template_id) {
    const onboardingParam = gate.onboarding_id ? `&onboardingId=${gate.onboarding_id}` : '';
    return `/onboarding/${gate.flow_template_id}?${onboardingParam.slice(1)}`;
  }
  return '/onboarding';
}
